import { useState, useEffect } from "react";
import { 
  useGetAvailableClaimsQuery, 
  useAddClaimAccessMutation, 
  useRemoveClaimAccessMutation,
  useAddCenterAccessMutation,
  useRemoveCenterAccessMutation
} from "../../../services/ProfileService";
import { useGetCentersQuery } from "../../../services/ConfigurationService";
import { ProfileView } from "../../../services/ProfileService/types/profileTypes";
import { transformClaimsAccess, transformAvailableClaims, transformAvailableCenters } from "../utils/profileUtils";

interface UseProfileDataProps {
  profile?: ProfileView;
  refreshProfile?: () => void;
}

export const useProfileData = ({ profile, refreshProfile }: UseProfileDataProps) => {
  const [role, setRole] = useState("");

  // Queries
  const { data: availableClaimsData, isLoading: isClaimsLoading } = useGetAvailableClaimsQuery();
  const { data: availableCentersData } = useGetCentersQuery(
    { company: profile?.organization ?? "" }, 
    { skip: !profile?.organization }
  );

  // Mutations
  const [addClaimAccess] = useAddClaimAccessMutation();
  const [removeClaimAccess] = useRemoveClaimAccessMutation();
  const [addCenterAccess] = useAddCenterAccessMutation();
  const [removeCenterAccess] = useRemoveCenterAccessMutation();

  // Transform data
  const availableClaims = transformAvailableClaims(availableClaimsData);
  const claims = transformClaimsAccess(profile?.claimsAccess);
  const availableCenters = transformAvailableCenters(availableCentersData);
  const centers = profile?.centerAccess ? profile.centerAccess : [];

  // Update role when profile changes
  useEffect(() => {
    if (profile?.role) setRole(profile.role);
  }, [profile?.role]);

  // Handlers
  const handleAddClaim = async (claim: { value: string; name: string }) => {
    if (!profile?.identity) return;
    try {
      await addClaimAccess({ 
        identity: profile.identity, 
        claimValue: claim.value, 
        claimName: claim.name 
      }).unwrap();
      refreshProfile?.();
    } catch (e) {
      console.error('Failed to add claim:', e);
    }
  };

  const handleDeleteClaim = async (claim: { value: string; name: string }) => {
    if (!profile?.identity) return;
    try {
      await removeClaimAccess({ 
        identity: profile.identity, 
        claimValue: claim.value, 
        claimName: claim.name 
      }).unwrap();
      refreshProfile?.();
    } catch (e) {
      console.error('Failed to remove claim:', e);
    }
  };

  const handleAddCenter = async (center: { code: string; name: string }) => {
    if (!profile?.identity) return;
    try {
      await addCenterAccess({ 
        identity: profile.identity, 
        centerCode: center.code, 
        centerName: center.name 
      }).unwrap();
      refreshProfile?.();
    } catch (e) {
      console.error('Failed to add center:', e);
    }
  };

  const handleDeleteCenter = async (center: { code: string; name: string }) => {
    if (!profile?.identity) return;
    try {
      await removeCenterAccess({ 
        identity: profile.identity, 
        centerCode: center.code, 
        centerName: center.name 
      }).unwrap();
      refreshProfile?.();
    } catch (e) {
      console.error('Failed to remove center:', e);
    }
  };

  const handleRoleChange = async (newRole: string) => {
    setRole(newRole);
    // Role change logic would go here if needed
    refreshProfile?.();
  };

  return {
    // Data
    role,
    setRole,
    availableClaims,
    claims,
    availableCenters,
    centers,
    isClaimsLoading,
    
    // Transformed data with consistent naming
    transformedClaimsAccess: claims,
    transformedAvailableClaims: availableClaims,
    transformedAvailableCenters: availableCenters,
    transformedCenterAccess: centers,

    // Handlers with consistent naming
    handleRoleChange,
    handleAddClaim,
    handleDeleteClaim,
    handleAddCenter,
    handleDeleteCenter,
    
    // Alternative names for consistency with existing code
    handleAddClaimAccess: handleAddClaim,
    handleDeleteClaimAccess: handleDeleteClaim,
    handleAddCenterAccess: handleAddCenter,
    handleDeleteCenterAccess: handleDeleteCenter,
  };
};